import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  Box, Button, Chip, IconButton, LinearProgress, Stack, Tooltip, Typography,
} from "@mui/material";
import { DifficultyChip, PALETTE, SectionPaper } from "./shared";
import CourseLangLogo from "./CourseLangLogo";

export const COURSE_CARD_MIN_HEIGHT = 300;

type Props = {
  course: any;
  onOpen: () => void;
  onEnroll: () => void;
  onToggleBookmark: () => void;
};

/** Shared course card used by CoursesView and BookmarksView so they stay pixel-identical. */
export default function CourseCard({ course, onOpen, onEnroll, onToggleBookmark }: Props) {
  const progress = course.enrollment?.progressPercent ?? 0;
  const description =
    course.shortDescription || course.description || "Start learning with structured lessons and hands-on practice.";

  return (
    <SectionPaper
      sx={{
        width: "100%",
        minHeight: COURSE_CARD_MIN_HEIGHT,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "box-shadow 0.15s, transform 0.15s",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
      }}
    >
      {/* Banner */}
      <Box
        onClick={onOpen}
        sx={{
          position: "relative",
          height: 68,
          flexShrink: 0,
          background: "#eff6ff",
          borderBottom: `1px solid ${PALETTE.border}`,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <CourseLangLogo language={course.language || course.programmingLanguage} category={course.category} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            {course.hasCertificate && (
              <Tooltip title="Certificate available">
                <SchoolOutlinedIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
              </Tooltip>
            )}
            {course.isFeatured && (
              <Chip
                size="small"
                label="Featured"
                sx={{ fontSize: 10, height: 20, bgcolor: "#fef3c7", color: "#d97706", fontWeight: 700 }}
              />
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Body */}
      <Box onClick={onOpen} sx={{ flex: 1, display: "flex", flexDirection: "column", px: 1.5, pt: 1.25, pb: 1 }}>
        <Typography
          sx={{
            fontWeight: 800, fontSize: 14, color: "primary.main", lineHeight: 1.35, mb: 0.5,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            minHeight: 38,
          }}
        >
          {course.title}
        </Typography>
        <Typography
          sx={{
            fontSize: 11, color: PALETTE.textSecondary, lineHeight: 1.45, mb: 1,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            minHeight: 32,
          }}
        >
          {description}
        </Typography>

        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: "wrap", minHeight: 24 }}>
          {course.difficulty && <DifficultyChip difficulty={course.difficulty} />}
          {course.language && (
            <Chip
              size="small"
              label={course.language}
              sx={{ fontSize: 10, height: 22, textTransform: "capitalize" }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mb: course.enrolled ? 1 : 0, flexWrap: "wrap" }}>
          <MetaItem icon={<TimerOutlinedIcon sx={{ fontSize: 13 }} />} label={`${course.estimatedHours ?? 0}h`} />
          <MetaItem icon={<PlayCircleOutlineIcon sx={{ fontSize: 13 }} />} label={`${course.totalLessons ?? 0} lessons`} />
          {course.rating > 0 && (
            <MetaItem icon={<StarOutlinedIcon sx={{ fontSize: 13, color: "#f59e0b" }} />} label={course.rating.toFixed(1)} />
          )}
        </Stack>

        {course.enrolled && (
          <Box sx={{ mt: "auto" }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
              <Typography sx={{ fontSize: 10, color: PALETTE.textSecondary, fontWeight: 600 }}>Progress</Typography>
              <Typography
                sx={{ fontSize: 10, fontWeight: 700, color: progress >= 100 ? "success.main" : "primary.main" }}
              >
                {progress}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={progress >= 100 ? "success" : "primary"}
              sx={{ height: 5, borderRadius: 3 }}
            />
          </Box>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 0, mt: "auto", flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {course.enrolled ? (
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={onOpen}
              sx={{ textTransform: "none", fontSize: 12, height: 32, borderRadius: "8px", fontWeight: 700 }}
            >
              {course.enrollment?.isCompleted ? "Review" : "Continue"}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              fullWidth
              onClick={(e) => { e.stopPropagation(); onEnroll(); }}
              sx={{ textTransform: "none", fontSize: 12, height: 32, borderRadius: "8px", fontWeight: 700 }}
            >
              Enroll Free
            </Button>
          )}
          <Tooltip title={course.bookmarked ? "Remove bookmark" : "Bookmark"}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onToggleBookmark(); }}
              sx={{ border: `1px solid ${PALETTE.borderDark}`, borderRadius: "8px", width: 32, height: 32, flexShrink: 0 }}
            >
              {course.bookmarked ? <BookmarkIcon color="primary" fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </SectionPaper>
  );
}

function MetaItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.4}>
      <Box sx={{ color: PALETTE.textMuted, display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, color: PALETTE.textSecondary, whiteSpace: "nowrap" }}>{label}</Typography>
    </Stack>
  );
}
